import {
  Button,
  Flex,
  Heading,
  Icon,
  Input,
  Spinner,
  Text,
} from '@chakra-ui/react'
import { Pencil, Plus, Trash2 } from 'lucide-react'
import React, { useEffect, useMemo, useState } from 'react'

import type { ToastLike } from '@mangovoice/mango-ui'
import type { PbxLocation } from '@mangovoice/types'

import type { PbxLocationsApi } from '../api/pbxLocationsApi.types'
import { LocationDeleteConfirmModal } from '../components/LocationDeleteConfirmModal'
import { LocationFormModal } from '../components/LocationFormModal'
import { useGetPbxLocationsQuery } from '../queries'
import { text as tfallback } from '../types'
import type { PbxLocationsTranslateFn } from '../types'
import { formatE911Address } from '../utils/pbxLocations.utils'

export interface PbxLocationsPageProps {
  api: PbxLocationsApi
  pbxId: number | undefined
  toast: ToastLike
  t?: PbxLocationsTranslateFn
}

const COLUMN_FLEX = {
  name: 1.2,
  extensions: 0.6,
  number: 1.2,
  address: 1.5,
  actions: 0.8,
}

export const PbxLocationsPage: React.FC<PbxLocationsPageProps> = ({
  api,
  pbxId,
  toast,
  t,
}) => {
  const [searchInput, setSearchInput] = useState('')
  const [debouncedSearch, setDebouncedSearch] = useState('')
  const [isFormOpen, setIsFormOpen] = useState(false)
  const [editingLocation, setEditingLocation] = useState<PbxLocation | null>(
    null,
  )
  const [deletingLocation, setDeletingLocation] = useState<PbxLocation | null>(
    null,
  )

  // Debounce the search input — keeps the query from spamming the API while
  // the admin is typing.
  useEffect(() => {
    const handle = setTimeout(() => {
      setDebouncedSearch(searchInput.trim())
    }, 250)
    return () => clearTimeout(handle)
  }, [searchInput])

  const { data, isFetching, isError, refetch } = useGetPbxLocationsQuery({
    api,
    pbxId,
    search: debouncedSearch,
    ordering: 'name',
    options: {
      enabled: Boolean(pbxId),
    },
  })

  const locations: PbxLocation[] = useMemo(() => data?.results ?? [], [data])

  const handleAddLocation = () => {
    setEditingLocation(null)
    setIsFormOpen(true)
  }

  const handleEditLocation = (location: PbxLocation) => {
    setEditingLocation(location)
    setIsFormOpen(true)
  }

  const handleCloseForm = () => {
    setIsFormOpen(false)
    setEditingLocation(null)
  }

  const handleSaved = () => {
    refetch()
  }

  const handleStartDelete = (location: PbxLocation) => {
    setDeletingLocation(location)
  }

  const handleCloseDelete = () => {
    setDeletingLocation(null)
  }

  const handleDeleted = () => {
    refetch()
  }

  const renderRow = (location: PbxLocation) => {
    const numberLabel =
      location.phone_number?.number_for_display ??
      location.phone_number?.number ??
      null
    const address = formatE911Address(location)

    return (
      <Flex
        key={location.id}
        flexDirection="row"
        padding="0.75rem 1rem"
        borderBottom="1px solid"
        borderColor="border.default"
        alignItems="center"
        gap="0.75rem"
      >
        <Text flex={COLUMN_FLEX.name} fontWeight="semibold">
          {location.name}
        </Text>
        <Text flex={COLUMN_FLEX.extensions}>{location.extension_count}</Text>
        {numberLabel ? (
          <Text flex={COLUMN_FLEX.number}>{numberLabel}</Text>
        ) : (
          <Text flex={COLUMN_FLEX.number} color="red.fg" fontStyle="italic">
            {tfallback(
              t,
              'pbxLocations.notConfiguredE911Number',
              'No E911 number assigned',
            )}
          </Text>
        )}
        {address ? (
          <Text flex={COLUMN_FLEX.address}>{address}</Text>
        ) : (
          <Text flex={COLUMN_FLEX.address} color="red.fg" fontStyle="italic">
            {tfallback(
              t,
              'pbxLocations.notConfiguredE911Address',
              'No E911 address on file',
            )}
          </Text>
        )}
        <Flex
          flex={COLUMN_FLEX.actions}
          flexDirection="row"
          gap="0.5rem"
          justifyContent="flex-end"
        >
          <Button
            variant="subtle"
            colorPalette="neutral"
            size="sm"
            rounded="lg"
            onClick={() => handleEditLocation(location)}
          >
            <Icon as={Pencil} boxSize={3.5} />
            {tfallback(t, 'pbxLocations.edit', 'Edit')}
          </Button>
          <Button
            variant="subtle"
            colorPalette="error"
            size="sm"
            rounded="lg"
            onClick={() => handleStartDelete(location)}
          >
            <Icon as={Trash2} boxSize={3.5} />
            {tfallback(t, 'pbxLocations.delete', 'Delete')}
          </Button>
        </Flex>
      </Flex>
    )
  }

  const renderTableBody = () => {
    if (isFetching && locations.length === 0) {
      return (
        <Flex padding="2rem 1rem" justifyContent="center" alignItems="center">
          <Spinner />
        </Flex>
      )
    }
    if (isError) {
      return (
        <Flex padding="1rem" color="red.fg">
          <Text>
            {tfallback(
              t,
              'pbxLocations.loadError',
              'Failed to load locations.',
            )}
          </Text>
        </Flex>
      )
    }
    if (locations.length === 0) {
      return (
        <Flex padding="2rem 1rem" justifyContent="center" alignItems="center">
          <Text>
            {tfallback(
              t,
              'pbxLocations.empty',
              'No locations configured for this PBX yet.',
            )}
          </Text>
        </Flex>
      )
    }
    return <>{locations.map(renderRow)}</>
  }

  return (
    <>
      <Flex width="100%" flexShrink={0} borderRadius="8px">
        <Flex
          flex={1}
          borderRadius="8px"
          backgroundColor="bg.default"
          borderWidth="1px"
          borderColor="border.default"
          alignSelf="stretch"
          flexDirection="column"
          flexShrink={0}
        >
          <Flex
            alignSelf="stretch"
            padding="16px"
            flexDirection="column"
            backgroundColor="bg.default"
            borderTopLeftRadius="8px"
            borderTopRightRadius="8px"
          >
            <Flex
              flexDirection="row"
              justifyContent="space-between"
              alignItems="center"
              alignSelf="stretch"
            >
              <Heading color="fg.default" fontSize="2xl">
                {tfallback(t, 'pbxLocations.header', 'Locations')}
              </Heading>
              <Button
                colorPalette="brand"
                size="sm"
                rounded="lg"
                onClick={handleAddLocation}
                disabled={!pbxId}
              >
                <Icon as={Plus} boxSize={4} color="colorPalette.contrast" />
                {tfallback(t, 'pbxLocations.addLocation', 'Add Location')}
              </Button>
            </Flex>
            <Text marginTop="0.5rem" color="fg.default" fontSize="sm">
              {tfallback(
                t,
                'pbxLocations.headerSubtext',
                'Manage office locations on this PBX. Each location maps to an E911 phone number that will be used as the caller ID for emergency calls.',
              )}
            </Text>
            <Flex marginTop="0.75rem" maxWidth="24rem">
              <Input
                value={searchInput}
                placeholder={tfallback(
                  t,
                  'pbxLocations.searchPlaceholder',
                  'Search locations',
                )}
                onChange={(e) => setSearchInput(e.target.value)}
                size="sm"
              />
            </Flex>
          </Flex>
          <Flex
            flexDirection="column"
            borderTop="1px solid"
            borderColor="border.default"
          >
            <Flex
              flexDirection="row"
              padding="0.75rem 1rem"
              borderBottom="1px solid"
              borderColor="border.default"
              backgroundColor="bg.panel"
              alignItems="center"
              gap="0.75rem"
            >
              <Text
                flex={COLUMN_FLEX.name}
                fontWeight="semibold"
                color="fg.default"
              >
                {tfallback(t, 'pbxLocations.column.name', 'Name')}
              </Text>
              <Text
                flex={COLUMN_FLEX.extensions}
                fontWeight="semibold"
                color="fg.default"
              >
                {tfallback(
                  t,
                  'pbxLocations.column.extensionCount',
                  'Extensions',
                )}
              </Text>
              <Text
                flex={COLUMN_FLEX.number}
                fontWeight="semibold"
                color="fg.default"
              >
                {tfallback(t, 'pbxLocations.column.e911Number', 'E911 Number')}
              </Text>
              <Text
                flex={COLUMN_FLEX.address}
                fontWeight="semibold"
                color="fg.default"
              >
                {tfallback(
                  t,
                  'pbxLocations.column.e911Address',
                  'E911 Address',
                )}
              </Text>
              <Text
                flex={COLUMN_FLEX.actions}
                fontWeight="semibold"
                color="fg.default"
                textAlign="right"
              >
                {tfallback(t, 'pbxLocations.column.actions', 'Actions')}
              </Text>
            </Flex>
            {renderTableBody()}
          </Flex>
        </Flex>
      </Flex>

      {pbxId && isFormOpen && (
        <LocationFormModal
          api={api}
          isOpen={isFormOpen}
          onClose={handleCloseForm}
          pbxId={pbxId}
          location={editingLocation ?? undefined}
          onSaved={handleSaved}
          toast={toast}
          t={t}
        />
      )}

      {deletingLocation && (
        <LocationDeleteConfirmModal
          api={api}
          isOpen={Boolean(deletingLocation)}
          onClose={handleCloseDelete}
          location={deletingLocation}
          onDeleted={handleDeleted}
          toast={toast}
          t={t}
        />
      )}
    </>
  )
}
